// eslint:disable:no-unused-expression
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { api } from 'app/area/import/api';
import * as actions from './action-types';
import {
  RequestDeleteImport, ReceiveDeleteImport, FailDeleteImport,
  RequestImportList
} from 'app/area/import/action-types';
import { SuccessNotification, ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { createDescriptionSelector } from 'app/infrastructure/translate/translate';
import { importTranslations, endpoint, itemsPerPage } from 'app/area/import/constants';
import { ImportFormData } from './types';
import { ImportState } from './types-store';
import { RequestUpdateImport } from 'app/area/import/action-types';
import { UiState } from 'app/infrastructure/ui/form/types';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { createUploader } from 'app/infrastructure/remote-data/http';

export const actionCreators = {

  loadImportList: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const importState = getState().import as ImportState;

      if (importState.fetchingImportList) {
        return;
      }

      const maxResults = importState?.importList?.length ?? itemsPerPage;

      dispatch(new RequestImportList());
      try {
        const importList = await api.loadImportList(0, maxResults);
        dispatch(new actions.ReceiveImportList(importList));
      } catch {
        dispatch(new actions.FailImportList());
      }
    },

  loadMoreImportList: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const importState = getState().import as ImportState;

      if (importState.fetchingImportList) {
        return;
      }

      const startIndex = importState?.importList?.length ?? 0;

      dispatch(new actions.RequestMoreImportListItems());
      try {
        const importList = await api.loadImportList(startIndex, itemsPerPage);
        dispatch(new actions.ReceiveMoreImportListItems(importList, importList?.length !== itemsPerPage));
      } catch {
        dispatch(new actions.FailMoreImportListItems());
      }
    },

  loadEditImport: (importCaseId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestLoadEditImport(importCaseId));
      try {
        const response = await api.loadEditImport(importCaseId);

        dispatch(new actions.ReceiveLoadEditImport(response));
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
        dispatch(new actions.FailLoadEditImport(importCaseId));
      }
    },

  loadImportDetails: (importCaseId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestLoadImportDetails(importCaseId));
      try {
        const response = await api.loadEditImport(importCaseId);

        dispatch(new actions.ReceiveLoadImportDetails(response));
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
        dispatch(new actions.FailLoadImportDetails(importCaseId));
      }
    },

  clearEditImport: (importFileId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.ClearEditImport(importFileId));
    },

  saveImport: (importFormData: ImportFormData, file: File, onClose: () => any):
    ActionDispatching<any, ApplicationState> => async (dispatch, getState) => {
      try {
        dispatch(new actions.RequestAddImport());

        const { texts } = getState().import;
        const translationDescription = createDescriptionSelector(texts);

        const toastMessage = toastFactory();

        const xhr = await createUploader(endpoint.caseImport);
        const data = new FormData();

        data.append('ImportPackage', file, file.name);
        data.append('ImportSet', importFormData.importset.v);
        data.append('Comments', importFormData.comments);

        xhr.onload = () => {
          if (xhr.status === 200) {
            dispatch(new actions.ReceiveAddImport());
            toastMessage.dispatchSuccess(translationDescription(importTranslations.import_success));
            onClose();
          } else {
            const error = JSON.parse(xhr.response);
            toastMessage.close();

            error && error.messages && error.messages.userMessages
              && error.messages.userMessages.forEach(err =>
                dispatch(new ErrorNotification(err.message))
              );

            error && error.messages && error.messages.validationErrors && error.messages.validationErrors
              .filter(item => item.errors && item.errors.length > 0)
              .map(errors => errors && errors.errors.forEach(err =>
                dispatch(new ErrorNotification(err.errorMessage))
              ));

            dispatch(new actions.FailAddImport());
          }
        };

        xhr.send(data);
      } catch (err) {
        dispatch({
          type: 'ERROR',
          error: err
        });

        dispatch(new actions.FailAddImport());
      }
    },

  updateImport: (importFileId: number, serializeData: ImportFormData):
    ActionDispatching<any, ApplicationState> => async (dispatch, getState) => {
      dispatch(new RequestUpdateImport(importFileId));

      try {
        const areaState = getState().import;
        const translationDescription = createDescriptionSelector(areaState.texts);

        const toastMessage = toastFactory();

        const importCaseUpdateData = {
          importFileId,
          importSet: serializeData.importset.v,
          ...(serializeData.comments && {
            comments: serializeData.comments
          })
        };

        await api.updateImport(importCaseUpdateData);

        dispatch(new actions.ReceiveUpdateImport(importFileId, serializeData));

        toastMessage.dispatchSuccess(
          translationDescription(importTranslations.import_update_success));

      } catch (err) {
        dispatch({
          type: 'ERROR',
          error: err
        });

        dispatch(new actions.FailUpdateImport(importFileId));
      }
    },

  retryImport: (importCaseId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestRetryImport(importCaseId));
      try {
        await api.retryImport(importCaseId);
        const areaState = getState().import as UiState;
        const translationDescription = createDescriptionSelector(areaState.texts);

        dispatch(new SuccessNotification(
          translationDescription(importTranslations.import_retry_success)
        ));

        dispatch(new actions.ReceiveRetryImport(importCaseId));
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
        dispatch(new actions.FailRetryImport(importCaseId));
      }
    },

  deleteImport: (importCase: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestDeleteImport(importCase));

      try {
        await api.deleteImport(importCase) as any;
        const areaState = getState().import as UiState;
        const translationDescription = createDescriptionSelector(areaState.texts);

        dispatch(new SuccessNotification(
          translationDescription(importTranslations.import_delete_success)
        ));

        dispatch(new ReceiveDeleteImport(importCase));
      } catch (err) {
        dispatch({
          type: 'ERROR',
          error: err
        });

        dispatch(new FailDeleteImport(importCase));
      }
    },

  clearImportCase: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.ClearImportCase());
    }
};
